import {List, Set} from 'immutable';
import isNil from 'lodash/isNil';
import moment, {Moment} from 'moment';

import {Data, RecordProps} from '@store/@types/util';
import CoachingReviewFeedback from 'records/CoachingReviewFeedback';
import {validateReviewTypeIds} from 'records/CoachingReviewType';
import {validate as validateMeetingType} from 'records/MeetingType';
import {requiredMessage} from 'utils/const';
import withinDateRange from 'utils/withinDateRange';
import validateRequiredProps from 'utils/validateRequiredProps';

import CoachingParticipant, {validate as validateParticipant} from './CoachingParticipant';
import CoachingContentDetail, {validate as validateContentDetail} from './CoachingContentDetail';
import EventSchedule, {ScheduleId, validate as validateSchedule} from './EventSchedule';
import MeetingType from './MeetingType';
import {Map, Record} from 'immutable';
import {ReviewId} from 'records/ReviewId';
import CoachingTodo from 'records/CoachingTodo';

const requiredProps = ['objective', 'name', 'schedule'];

export function validate(plan) {
  const errors = validateRequiredProps(plan, requiredProps);
  if (plan.schedule) {
    errors.schedule = validateSchedule(plan.schedule);
  }
  const coachErrors = validateParticipant(plan.coach);
  if (Object.keys(coachErrors).length > 0) {
    errors.coach = coachErrors;
  }
  const recipientErrors = validateParticipant(plan.recipient);
  if (Object.keys(recipientErrors).length > 0) {
    errors.recipient = recipientErrors;
  }
  const contentDetailErrors = plan.contentDetails.map(validateContentDetail);
  const hasContentDetailErrors = contentDetailErrors.some(errors => Object.keys(errors).length > 0);
  if (hasContentDetailErrors) {
    errors.contentDetails = contentDetailErrors.toArray();
  }
  const meetingTypeErrors = validateMeetingType(plan.defaultMeetingType);
  if (Object.keys(meetingTypeErrors).length > 0) {
    errors.meetingType = meetingTypeErrors;
  }

  const reviewTypeError = validateReviewTypeIds(plan.reviewTypeIds);
  if (reviewTypeError) {
    errors.reviewType = reviewTypeError;
  }
  if (!plan.useGenericQuestions && !plan.scorecardId) {
    errors.scorecardId = requiredMessage;
  }

  return errors;
}

export const planEnd = (projectedEnd, planCompleted, amountOfCompletedReviews) => {
  if (projectedEnd) return projectedEnd.format('MM/DD/Y');

  if (planCompleted && amountOfCompletedReviews === 0) return 'Plan ended (no reviews)';

  return 'No end date';
};

export type PlanId = number;

interface CoachingPlanInterface {
  readonly accountId: string;
  readonly coach: CoachingParticipant;
  readonly recipient: CoachingParticipant;
  readonly contentDetails: List<CoachingContentDetail>;
  readonly defaultMeetingType?: any;
  readonly id: PlanId;
  readonly name?: string;
  readonly objective?: string;
  readonly schedule?: EventSchedule;
  readonly scheduleId?: ScheduleId;
  readonly reviews?: List<CoachingReview>;
  readonly reviewFeedback?: any;
  readonly created: Moment;
  readonly completed: boolean;
  readonly adHoc: boolean;
  readonly livecoaching: boolean;
  readonly reviewTypeIds: Set<number>;
  readonly scorecardId?: number;
  readonly useGenericQuestions: boolean;
  readonly completedAt: Moment;
}

export default class CoachingPlan extends Data<CoachingPlanInterface>({
  completedAt: null,
  accountId: null,
  coach: null,
  contentDetails: null,
  defaultMeetingType: null,
  id: null,
  name: null,
  objective: null,
  recipient: null,
  schedule: null,
  scheduleId: null,
  reviews: null,
  reviewFeedback: null,
  created: null,
  completed: null,
  adHoc: false,
  livecoaching: false,
  reviewTypeIds: null,
  scorecardId: null,
  // this is just a dummy field needed by SessionAgendaStep
  useGenericQuestions: true,
}) {
  constructor(object: RecordProps<CoachingPlanInterface> & {meetingType?: any} = {}) {
    const reviewTypeIds: number[] = [];
    if (object.reviewTypeIds) {
      reviewTypeIds.push(...object.reviewTypeIds);
    }

    super({
      ...object,
      schedule: new EventSchedule(object.schedule),
      coach: new CoachingParticipant(object.coach),
      recipient: new CoachingParticipant(object.recipient),
      contentDetails: List(
        (object.contentDetails ?? []).map(detail => new CoachingContentDetail(detail))
      ),
      reviews: List((object.reviews ?? []).map(review => new CoachingReview(review))),
      reviewFeedback: List(
        (object.reviewFeedback ?? []).map(feedback => new CoachingReviewFeedback(feedback))
      ),
      defaultMeetingType: object.meetingType
        ? new MeetingType(object.meetingType)
        : object.defaultMeetingType
        ? new MeetingType(object.defaultMeetingType)
        : null,
      created: moment(object.created),
      completedAt: object.completedAt && moment(object.completedAt),
      reviewTypeIds: Set(reviewTypeIds),
      // allow initialization override by object
      useGenericQuestions: isNil(object.useGenericQuestions)
        ? isNil(object.scorecardId)
        : object.useGenericQuestions,
    });
  }

  unscheduled() {
    return this.adHoc || this.livecoaching;
  }

  /**
   * Returns the latest review
   */
  getNextReview(includeCompleted = false) {
    if (!this.reviews) return;

    const reviews = this.reviews.sort((l, r) => r.scheduleAt.valueOf() - l.scheduleAt.valueOf());
    return includeCompleted ? reviews.first() : reviews.find(r => r.status === 'SCHEDULED');
  }

  /**
   *  get all user id's associated with this plan
   *  TODO: Should probably(?) include review coach Ids
   */
  getUserIds() {
    return Set([this.coach.id, this.recipient.id]);
  }
  getSkillNames(char = ', ') {
    if (!this.contentDetails || this.contentDetails.size === 0) return '';

    return this.contentDetails.map(c => (c.skillName ? c.skillName : c.custom)).join(char);
  }
  getDefaultCoachingPlanObjective() {
    if (!this.recipient.info || !this.contentDetails || this.contentDetails.size === 0) return '';

    const skills = this.getSkillNames();

    return `${this.recipient.info.getFullName()} should improve skill${
      this.contentDetails.size > 1 ? 's' : ''
    }: ${skills}`;
  }
  getDefaultCoachingPlanShortName(now) {
    if (!this.contentDetails || this.contentDetails.size === 0 || !this.schedule) return 'Unnamed';

    const skills = this.getSkillNames();
    const effectiveEndDate =
      this.schedule.recurrenceCount || this.schedule.recurrenceEndDate
        ? this.schedule.getEffectiveEndDate()
        : null;

    return `${skills}, ${now.format('MMM YYYY')}${
      effectiveEndDate ? '-' + effectiveEndDate.format('MMM YYYY') : ''
    }`;
  }

  /**
   * Given two dates return whether the plan was active during that time
   * @param startDate
   * @param endDate
   */
  wasActiveDuringRange(startDate, endDate) {
    const reviewsCompletedDuringRange = this.reviews!.some(
      review => !!review.completedAt && withinDateRange([startDate, endDate], review.completedAt)
    );

    const planActiveDuringRange = withinDateRange([startDate, endDate], this.created);

    return reviewsCompletedDuringRange || planActiveDuringRange;
  }

  /**
   * Handles adjustment for coaching schedule fields mutated by the backend
   */
  fromApi() {
    // Returns a local time version of event schedule
    // Compares utc to local time and adjusts daysOfWeek as necessary
    const localSchedule = schedule => {
      // NOOP schedule is in coaches local time
      return schedule;
    };

    return this.update('schedule', localSchedule).updateIn(
      ['schedule', 'weeksBetween'],
      weeks => weeks + 1
    );
  }
}

export class CoachingReview extends Record({
  id: undefined,
  reviewTypeId: undefined,
  scheduleAt: undefined,
  completedAt: undefined,
  planId: undefined,
  coachUserId: undefined,
  meetingTypeId: undefined,
  meetingType: undefined,
  status: undefined,
  coachingPlan: undefined,
  todo: undefined,
  otherParticipantTodo: undefined,
  comment: undefined,
  callRecordingLink: undefined,
  reviewTypeAnswers: Map(),
  reminderSentAt: undefined,
  livecoachingStubs: 0,
  recipientHasAnsweredQuestions: undefined,
}) {
  readonly id: ReviewId;
  readonly reviewTypeId: string;
  readonly scheduleAt: Moment;
  readonly completedAt?: Moment;
  readonly planId: string;
  readonly coachUserId: string;
  readonly meetingTypeId: string;
  readonly meetingType: any; // TODO: Improve typing.
  readonly status: any; // TODO: Improve typing.
  readonly coachingPlan?: CoachingPlan;
  readonly todo?: CoachingTodo;
  readonly otherParticipantTodo?: CoachingTodo;
  readonly comment?: string;
  readonly callRecordingLink?: string;
  readonly reviewTypeAnswers?: any; // TODO: Improve typing.
  readonly reminderSentAt?: Moment;
  readonly livecoachingStubs: number;
  readonly recipientHasAnsweredQuestions: boolean;

  constructor(object: RecordProps<CoachingReview> = {}) {
    super({
      ...object,
      // scheduleAt is served in UTC by the API, so we adjust it to
      // the browser's local time here
      scheduleAt: object.scheduleAt ? moment.utc(object.scheduleAt).local() : undefined,
      completedAt: object.completedAt ? moment.utc(object.completedAt).local() : undefined,
      reminderSentAt: object.reminderSentAt ? moment.utc(object.reminderSentAt).local() : undefined,
      meetingType: object.meetingType ? new MeetingType(object.meetingType) : undefined,
      todo: object.todo ? new CoachingTodo(object.todo) : undefined,
      otherParticipantTodo: object.otherParticipantTodo
        ? new CoachingTodo(object.otherParticipantTodo)
        : undefined,
      coachingPlan: object.coachingPlan ? new CoachingPlan(object.coachingPlan) : undefined,
    });
  }

  canSendReminder() {
    if (this.reminderSentAt) {
      const nextTime = this.reminderSentAt.clone().add(1, 'day');
      const today = moment();
      return today.isAfter(nextTime);
    }

    return true;
  }

  // Only works with the "Upcoming Review" version of coaching reviews.
  get rep() {
    if (!this.coachingPlan) {
      console.warn('review.rep getter not valid for this review');
    }

    return this.coachingPlan && this.coachingPlan.recipient && this.coachingPlan.recipient.info;
  }

  // Only works with the "Upcoming Review" version of coaching reviews.
  get coach() {
    if (!this.coachingPlan) {
      console.warn('review.coach getter not valid for this review');
    }

    return this.coachingPlan && this.coachingPlan.coach && this.coachingPlan.coach.info;
  }

  // Only works with the "Upcoming Review" version of coaching reviews.
  todoStatus() {
    if (this.todo!.done && this.otherParticipantTodo!.done) {
      return 'READY';
    }

    if (!this.todo!.done) {
      return 'TODO';
    }

    return 'OTHER_TODO';
  }
}
